import React, { useState, useEffect } from 'react';
import { Mail, AlertTriangle, RefreshCw, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export const EmailVerificationBanner = () => {
  const { user, refreshUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Poll for verification status every 15s
  useEffect(() => {
    if (!user || user.is_verified || dismissed) return;

    const interval = setInterval(async () => {
      const fresh = await refreshUser();
      if (fresh?.is_verified) {
        setDismissed(true);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [user, dismissed, refreshUser]);

  if (!user || user.is_verified || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await api.post('/auth/resend-verification');
      setSent(true);
      setTimeout(() => setSent(false), 10000);
    } catch (e) {
      console.error('Failed to resend:', e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 flex flex-col gap-3"
      data-testid="email-verification-banner"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-amber-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-text-primary">Please verify your email to continue</p>
            <p className="text-xs text-text-muted mt-0.5">
              We sent a verification link to <span className="text-text-secondary font-medium">{user.email}</span>. Click it to activate your account.
            </p>
          </div>
        </div>
        <button
          onClick={handleResend}
          disabled={sending || sent}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 rounded-lg text-sm font-medium transition disabled:opacity-50 flex-shrink-0"
          data-testid="resend-verification-btn"
        >
          {sent ? (
            <>
              <CheckCircle className="w-4 h-4" />
              Email Sent
            </>
          ) : sending ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Resend Verification Email
            </>
          )}
        </button>
      </div>
      <p className="text-xs text-text-muted/70 pl-[52px]">
        Don't see the email? Check your <span className="text-amber-300/80 font-medium">spam or junk folder</span>. It may take a minute to arrive.
      </p>
    </div>
  );
};
